import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DropZone from "../components/ui/DropZone";
import Input from "../components/ui/Input";
import SelectBox from "../components/ui/SelectBox";
import Button from "../components/ui/Button";
import FastAPIClient from "../services/apiClient";
import { fetchCurrencies, fetchBranch, API_BASE_PATH, API_ROOT_PATH } from "../apiHelper";
import { Image } from 'lucide-react';

const client = new FastAPIClient();

const initialState = {
    store_id: 0,
    member_id: 0,
    setccurr: '',
    identifier: '',
    storetype: '',
    staddress_id: 0,
    address1: '',
    zipcode: '',
    city: '',
    state: '',
    email1: '',
    phone1: '',
    firstname: '',
    lastname: '',
    persontitle: '',
    storeimage_url: '',
};

const EditStore = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Extract store_id from location state
    const { store_id } = location.state || {};

    const [storeObject, setStoreObject] = useState({
        store_id: store_id || 0,
        member_id: 0,
        setccurr: '',
        identifier: '',
        storetype: '',
        staddress_id: 0,
        address1: '',
        zipcode: '',
        city: '',
        state: '',
        email1: '',
        phone1: '',
        firstname: '',
        lastname: '',
        persontitle: '',
        storeimage_url: '',
    });

    const [toast, showToast] = useState({ message: '', show: false, toastType: 1 });
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!store_id) {
            console.error("No store_id provided for editing");
            navigate("/branches"); // Redirect back if no ID
            return;
        }

        const lcUser = localStorage.getItem("user");
        const user = lcUser ? JSON.parse(lcUser) : null;

        const loadInitialData = async () => {
            try {
                // 1. Fetch Currencies
                const fetchedCurrencies = await fetchCurrencies();
                const currencyOptions = fetchedCurrencies.map((c) => ({
                    label: `${c.setccurr} - ${c.setcnote}`,
                    value: `${c.setccurr}`
                }));
                setCurrencies(currencyOptions);

                // 2. Fetch Branch Details
                const branchDataArray = await fetchBranch(store_id);
                if (branchDataArray && branchDataArray.length > 0) {
                    const data = branchDataArray[0];
                    // console.log(data)

                    // Map API response to our form state
                    setStoreObject(prev => ({
                        ...prev,
                        member_id: user?.users_id || 0,
                        identifier: data.store_name,
                        storetype: data.storetype,
                        setccurr: data.currency,
                        address1: data.address1,
                        city: data.city,
                        state: data.state,
                        zipcode: data.zipcode,
                        email1: data.email1,
                        phone1: data.phone1,
                        firstname: data.firstname,
                        lastname: data.lastname,
                        persontitle: data.persontitle,
                    }));
                }
            } catch (err) {
                console.error("Error loading branch data:", err);
                showToast({ message: "Failed to load branch details.", show: true, toastType: 0 });
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [store_id, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setStoreObject(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUploadComplete = (uploadedFiles) => {
        if (uploadedFiles?.length > 0) {
            setStoreObject(prev => ({ ...prev, storeimage_url: uploadedFiles[0].url }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            store: {
                store_id: storeObject.store_id,
                member_id: storeObject.member_id,
                setccurr: storeObject.setccurr,
                identifier: storeObject.identifier,
                storetype: storeObject.storetype,
            },
            staddress: {
                address1: storeObject.address1,
                member_id: storeObject.member_id,
                zipcode: storeObject.zipcode,
                city: storeObject.city,
                state: storeObject.state,
                email1: storeObject.email1,
                phone1: storeObject.phone1,
                firstname: storeObject.firstname,
                lastname: storeObject.lastname,
                persontitle: storeObject.persontitle,
            }
        };

        try {
            // console.log(payload)
            // Using a PUT or POST depending on your backend update route
            await client.postData(`${API_BASE_PATH}/store/edit-store`, payload);
            showToast({ message: "Branch updated successfully!", show: true, toastType: 1 });
            setStoreObject(initialState);
            setTimeout(() => navigate(-1), 1500);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || "Failed to update branch.";
            showToast({ message: errorMessage, show: true, toastType: 0 });
        }
    };

    const businessTypeOptions = [
        { label: "Retail: RTL", value: "RTL" },
        { label: "Warehouse: WHS", value: "WHS" },
        { label: "Education: EDU", value: "EDU" },
        { label: "Others: OTR", value: "OTR" },
    ];

    if (loading) return <div className="p-10 text-center">Loading branch details...</div>;

    return (
        <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto">
                    <div className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Edit Branch</h1>
                        <p className="text-gray-600 text-md">Update the details for <strong>{storeObject.identifier}</strong></p>
                    </div>

                    {toast.show && (
                        <div className={`p-3 mb-4 rounded-md text-sm font-medium ${toast.toastType === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {toast.message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Branch Details</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input name="identifier" value={storeObject.identifier} onChange={handleInputChange} label="Branch Name" type="text" required />
                                <SelectBox name="storetype" value={storeObject.storetype} onChange={handleInputChange} label="Business Type" options={businessTypeOptions} required />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <SelectBox name="setccurr" value={storeObject.setccurr} onChange={handleInputChange} label="Default Currency" options={currencies} required />
                            </div>
                        </div>

                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Address Information</h2>
                            <div className="mb-4">
                                <Input name="address1" value={storeObject.address1} onChange={handleInputChange} label="Address" type="text" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <Input name="city" value={storeObject.city} onChange={handleInputChange} label="City" type="text" />
                                <Input name="state" value={storeObject.state} onChange={handleInputChange} label="State" type="text" />
                                <Input name="zipcode" value={storeObject.zipcode} onChange={handleInputChange} label="Zip Code" type="text" />
                            </div>
                        </div>

                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Contact Person</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input name="email1" value={storeObject.email1} onChange={handleInputChange} label="Email" type="email" />
                                <Input name="phone1" value={storeObject.phone1} onChange={handleInputChange} label="Phone" type="tel" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <Input name="persontitle" value={storeObject.persontitle} onChange={handleInputChange} label="Title" type="text" />
                                <Input name="firstname" value={storeObject.firstname} onChange={handleInputChange} label="First Name" type="text" />
                                <Input name="lastname" value={storeObject.lastname} onChange={handleInputChange} label="Last Name" type="text" />
                            </div>
                        </div>

                        <div className="flex justify-end pt-4">
                            <Button type="submit" variant="primary" size="lg">Update Branch</Button>
                        </div>
                    </form>
                </div>

                <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6">
                    <div className="p-4 mt-8 border border-gray-200 rounded-2xl flex flex-col items-center">
                        <div className="w-32 h-32 relative -mt-16 mb-4 border-4 border-white rounded-xl shadow-lg flex items-center justify-center bg-gray-100 overflow-hidden">
                            {storeObject.storeimage_url ? (
                                <img src={`${API_ROOT_PATH}${storeObject.storeimage_url}`} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                                <Image size={64} className="text-gray-400" />
                            )}
                        </div>
                        <h2 className="font-bold text-gray-700 text-lg">{storeObject.identifier || "Branch Name"}</h2>
                        <p className="text-gray-500 text-sm text-center">
                            {storeObject.address1} {storeObject.city && `, ${storeObject.city}`}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditStore;